import { useEffect, useRef, useState } from 'react';
import type { BannerCreative } from '../server/libs/siteSettings';

export const BANNER_CROSSFADE_DURATION_MS = 400;

export type CreativeLayer = {
  creative: BannerCreative;
  imageSrc: string;
};

export function useCreativeCrossfadeLayer(
  creative: BannerCreative,
  imageSrc: string,
) {
  const [activeLayer, setActiveLayer] = useState<CreativeLayer>({
    creative,
    imageSrc,
  });
  const [previousLayer, setPreviousLayer] = useState<CreativeLayer | null>(
    null,
  );
  const [isFading, setIsFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const activeRef = useRef<CreativeLayer>({ creative, imageSrc });

  useEffect(() => {
    activeRef.current = activeLayer;
  }, [activeLayer]);

  useEffect(() => {
    const nextLayer: CreativeLayer = { creative, imageSrc };
    const current = activeRef.current;
    const isSameCreative =
      current.creative.name === nextLayer.creative.name &&
      current.imageSrc === nextLayer.imageSrc;

    if (isSameCreative) {
      return;
    }

    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    let cancelled = false;
    let started = false;
    const startFade = () => {
      if (cancelled || started) return;
      started = true;
      setPreviousLayer(current);
      setActiveLayer(nextLayer);
      activeRef.current = nextLayer;
      setIsFading(false);
      rafRef.current = requestAnimationFrame(() => {
        if (cancelled) return;
        setIsFading(true);
        fadeTimerRef.current = setTimeout(() => {
          setPreviousLayer(null);
          setIsFading(false);
          fadeTimerRef.current = null;
        }, BANNER_CROSSFADE_DURATION_MS);
      });
    };

    const preloader = new window.Image();
    preloader.onload = startFade;
    preloader.onerror = startFade;
    preloader.src = imageSrc;

    const preloadFallbackRef = setTimeout(() => {
      startFade();
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(preloadFallbackRef);
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [creative, imageSrc]);

  return { activeLayer, previousLayer, isFading };
}
